// Slide export pixels are independent of the document's image slots.
export const EXPORT_IMAGE_PROFILES = Object.freeze({
  low: Object.freeze({width: 300, height: 225}),
  medium: Object.freeze({width: 600, height: 450}),
  high: null,
});
export const DEFAULT_EXPORT_IMAGE_QUALITY = 'low';
export const EXPORT_IMAGE_TIMEOUT_MS = 30000;

export function normalizeImageQuality(quality) {
  return Object.prototype.hasOwnProperty.call(EXPORT_IMAGE_PROFILES, quality)
    ? quality : DEFAULT_EXPORT_IMAGE_QUALITY;
}

export function fitImage(width, height, bounds, allowUpscale = false) {
  if (!(width > 0 && height > 0 && Number.isFinite(width) && Number.isFinite(height))) {
    throw new Error('Invalid slide image dimensions');
  }
  const scale = bounds
    ? Math.min(bounds.width / width, bounds.height / height, allowUpscale ? Infinity : 1)
    : 1;
  return {width: width * scale, height: height * scale};
}

export function exportImageDimensions(width, height, quality) {
  const size = fitImage(width, height, EXPORT_IMAGE_PROFILES[normalizeImageQuality(quality)]);
  return {width: Math.max(1, Math.round(size.width)), height: Math.max(1, Math.round(size.height))};
}

export function withImageTimeout(promise, timeoutMs = EXPORT_IMAGE_TIMEOUT_MS) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('Slide image loading timed out')), timeoutMs);
    Promise.resolve(promise).then(resolve, reject).finally(() => clearTimeout(timer));
  });
}

export function prepareExportImage(data, quality) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const timer = setTimeout(() => fail(new Error('Slide image decoding timed out')), EXPORT_IMAGE_TIMEOUT_MS);
    const cleanup = () => {
      clearTimeout(timer);
      image.onload = null;
      image.onerror = null;
    };
    const fail = error => {
      cleanup();
      reject(error);
    };
    image.onerror = () => fail(new Error('Could not decode slide image'));
    image.onload = () => {
      try {
        const sourceWidth = image.naturalWidth;
        const sourceHeight = image.naturalHeight;
        const {width, height} = exportImageDimensions(sourceWidth, sourceHeight, quality);
        let result = data;
        if (width !== sourceWidth || height !== sourceHeight) {
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const context = canvas.getContext('2d');
          if (!context) throw new Error('Could not create slide image canvas');
          context.imageSmoothingEnabled = true;
          context.imageSmoothingQuality = 'high';
          context.drawImage(image, 0, 0, width, height);
          result = canvas.toDataURL('image/png');
        }
        cleanup();
        resolve({data: result, width, height, sourceWidth, sourceHeight});
      } catch (error) {
        fail(error);
      }
    };
    try {
      image.src = data;
    } catch (error) {
      fail(error);
    }
  });
}
